import * as React from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';

// Only treat text as a mesh if it has OBJ vertices and faces.
export function looksLikeObj(text: string): boolean {
  return /^v\s/m.test(text) && /^f\s/m.test(text);
}

interface Props {
  obj: string;
  filename: string;
}

interface State {
  error: boolean;
}

export class MeshViewer extends React.Component<Props, State> {
  state: State = { error: false };
  mount = React.createRef<HTMLDivElement>();
  renderer?: THREE.WebGLRenderer;
  controls?: OrbitControls;
  frame = 0;
  onResize = () => {};
  downloadUrl = URL.createObjectURL(
    new Blob([this.props.obj], { type: 'text/plain' }),
  );

  componentDidMount() {
    try {
      this.setupScene();
    } catch (e) {
      // E.g. WebGL disabled. Keep the download link working.
      console.error(e);
      this.setState({ error: true });
    }
  }

  setupScene() {
    const mount = this.mount.current;
    if (!mount) {
      return;
    }
    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    const object = new OBJLoader().parse(this.props.obj);
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.computeVertexNormals();
        child.material = new THREE.MeshNormalMaterial({
          side: THREE.DoubleSide,
        });
      }
    });
    scene.add(object);

    // Frame the mesh regardless of its units or position.
    const box = new THREE.Box3().setFromObject(object);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3()).length();

    const fov = 50;
    const camera = new THREE.PerspectiveCamera(
      fov,
      width / height,
      size / 100,
      size * 10,
    );
    // Fit the bounding sphere inside the narrower of the vertical and horizontal fields of view.
    const halfFov = THREE.MathUtils.degToRad(fov / 2);
    const halfFovNarrow = Math.min(
      halfFov,
      Math.atan(Math.tan(halfFov) * camera.aspect),
    );
    const distance = size / 2 / Math.sin(halfFovNarrow);
    // Meshes are Z up, Y forward, X right. Start top down with forward pointing up on screen;
    // the tiny -Y offset avoids looking exactly along camera.up, which leaves the view unoriented.
    camera.up.set(0, 0, 1);
    camera.position
      .copy(center)
      .add(new THREE.Vector3(0, -0.001, 1).setLength(distance));

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setClearColor('#263238');
    this.renderer.setSize(width, height);
    mount.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(camera, this.renderer.domElement);
    this.controls.target.copy(center);

    this.onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      this.renderer!.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener('resize', this.onResize);

    const animate = () => {
      this.frame = requestAnimationFrame(animate);
      this.controls!.update();
      this.renderer!.render(scene, camera);
    };
    animate();
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.onResize);
    this.controls?.dispose();
    this.renderer?.dispose();
    URL.revokeObjectURL(this.downloadUrl);
  }

  render() {
    return (
      <div>
        {this.state.error ? (
          <p>Could not render mesh (is WebGL enabled?), see console.</p>
        ) : (
          <div ref={this.mount} style={{ width: '100%', height: '70vh' }} />
        )}
        <a
          className="button"
          href={this.downloadUrl}
          download={this.props.filename}
        >
          Download .obj
        </a>
      </div>
    );
  }
}
